/* eslint-disable @typescript-eslint/camelcase */
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { UserMeta, header } from '@core/model/common/UserMeta'
import { cid, container } from 'inversify-props'
import API from '@core/services/API'
import '@/DIContainer'
import { PrincipalMetaList } from '@core/model/common/PrincipalMetaList'
import { PrincipalMeta } from '@core/model/common/PrincipalMeta'
import { UserAdviserDetailsMeta } from '../sections/user-adviser-details-components/model/UserAdviserDetailsMeta'
import { PicodedAccount } from '@core/pages/account/model/PicodedAccountDTO'
import { AccountService } from '@core/pages/account/api'
import { IAdviserService } from '@master/services/IAdviserService'
import { UserMetaList } from '@core/model/common/UserMetaList'

const $api = container.get<API>('$api')
const adviserService = container.get<IAdviserService>(cid.IAdviserService)

const principalRequestHeaders = [
  '_oid',
  'company_code_oid',
  'company_details_name',
  'company_details_code',
  'adviser_code'
]

@Module({ namespaced: true })
export default class UserStore extends VuexModule {
  loading = false
  user: UserMeta = new UserMeta()
  userSystem: PicodedAccount = new PicodedAccount()
  originalUserName = ''
  productPrincipals: PrincipalMetaList = new PrincipalMetaList(principalRequestHeaders, [])
  userAdviserDetails: UserAdviserDetailsMeta = new UserAdviserDetailsMeta(adviserService)
  userList: UserMeta[] = []

  @Action({ rawError: true })
  async fetchUser (oid: string) {
    this.context.commit('setLoading', true)
    const user = await $api.account.loginMetaGet({
      _oid: oid
    })
    const { groups, accountNames } = await $api.account.loginInfoGet({
      accountID: oid
    })
    this.context.commit('setUser', { ...user.meta, groups })
    this.context.commit('setUserSystem', { ...user.meta, username: accountNames[0] })
    this.context.dispatch('fetchPrincipalList')
    this.context.commit('setLoading', false)
  }

  @Action({ rawError: true })
  async saveUser () {
    console.log('saveUser')
    this.context.commit('setLoading', true)
    this.user.status = this.userSystem.status
    this.user.systemrole = this.userSystem.systemrole
    await $api.account.meta({
      _oid: this.user.oid,
      meta: JSON.stringify(this.user.convertToRawData())
    })
    this.context.commit('setLoading', false)
  }

  @Action({ rawError: true })
  async changeUserName () {
    const result = await AccountService.changeUserName({
      oid: this.user.oid,
      username: this.userSystem.username
    })

    if (result) {
      this.context.commit('setUserSystem', this.userSystem)
    }
  }

  @Action({ rawError: true })
  async savePassword () {
    this.context.commit('setLoading', true)
    try {
      await $api.account.password({
        accountID: this.user.oid,
        newPassword: this.userSystem.newPassword,
        oldPassword: this.userSystem.oldPassword,
        confirmPassword: this.userSystem.confirmPassword
      })
    } finally {
      this.context.commit('clearPassword')
      this.context.commit('setLoading', false)
    }
  }

  @Action({ rawError: true })
  async fetchPrincipalList () {
    this.context.commit('setLoading', true)
    const res = await $api.adviserAgentCodes.list({
      query: 'adviser_oid = ?',
      queryArgs: JSON.stringify([this.user.oid]),
      headers: JSON.stringify(principalRequestHeaders)
    })
    this.context.commit('setProductPrincipals', res.data)
    this.context.commit('setLoading', false)
  }

  @Action({ rawError: true })
  async fetchAdviserDetails (adviser_oid: string) {
    this.context.commit('setLoading', true)
    const res = await $api.adviserDetails.getDetails({
      adviser_oid
    })

    const data = { ...res.data }

    const educationList = await $api.adviserDetails.getEducationList({
      adviser_oid
    })
    data.education_list = JSON.stringify(educationList?.data) || {}

    const qualificationList = await $api.adviserDetails.getQualificationList({
      adviser_oid
    })
    data.qualification_list = JSON.stringify(qualificationList?.data) || {}

    const employmentList = await $api.adviserDetails.getEmploymentList({
      adviser_oid
    })
    data.employment_list = JSON.stringify(employmentList?.data) || {}

    this.context.commit('setAdviserDetails', data)
    this.context.commit('setLoading', false)
  }

  @Action({ rawError: true })
  async saveProductPrincipal (principal: PrincipalMeta) {
    this.context.commit('setLoading', true)
    await $api.adviserAgentCodes.meta({
      _oid: principal.oid,
      meta: JSON.stringify({ adviser_code: principal.adviserCode })
    })
    this.context.dispatch('fetchPrincipalList')
    this.context.commit('setLoading', false)
  }

  @Action({ rawError: true })
  async fetchUserList () {
    if (this.userList.length > 0) return
    const res = await $api.account.list({
      headers: JSON.stringify(header),
      groupStatus: 'user'
    })
    const userMetaList: UserMetaList = new UserMetaList(res.data, header)

    this.context.commit('setUserList', userMetaList.list)
  }

  @Mutation
  setProductPrincipals (data: (string | number)[][]) {
    this.productPrincipals = new PrincipalMetaList(principalRequestHeaders, data)
  }

  @Mutation
  setAdviserDetails (adviserDetails: object) {
    this.userAdviserDetails.assignData(Object.keys(adviserDetails), Object.values(adviserDetails))
  }

  @Mutation
  clearPassword () {
    this.userSystem.newPassword = ''
    this.userSystem.confirmPassword = ''
  }

  @Mutation
  setUser (user: object) {
    this.user.assignData(Object.keys(user), Object.values(user))
  }

  @Mutation
  setUserSystem (user: object) {
    this.userSystem.assignData(user)
    this.originalUserName = this.userSystem.username
  }

  @Mutation
  setLoading (state: boolean) {
    this.loading = state
  }

  @Mutation
  setUserList (data: UserMeta[]) {
    this.userList = data
  }
}
